import React from 'react'

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Stack direction
   */
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  /**
   * Responsive direction configuration
   */
  responsive?: {
    sm?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
    md?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
    lg?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
    xl?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  }
  /**
   * Alignment along main axis
   */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
  /**
   * Alignment along cross axis
   */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'
  /**
   * Gap between items
   */
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Wrap behavior
   */
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse'
  /**
   * Stack content
   */
  children: React.ReactNode
}

const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'row',
      responsive,
      align = 'stretch',
      justify = 'start',
      gap = 'md',
      wrap = 'nowrap',
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'flex'
    ]

    const getDirectionClasses = (dir: string) => {
      const directionMap = {
        'row': 'flex-row',
        'column': 'flex-col',
        'row-reverse': 'flex-row-reverse',
        'column-reverse': 'flex-col-reverse'
      }
      return directionMap[dir as keyof typeof directionMap] || 'flex-row'
    }

    const alignClasses = {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline'
    }

    const justifyClasses = {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly'
    }

    const gapClasses = {
      none: [],
      xs: ['gap-1'],
      sm: ['gap-2'],
      md: ['gap-4'],
      lg: ['gap-6'],
      xl: ['gap-8']
    }

    const wrapClasses = {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse'
    }

    const responsiveClasses = responsive ? [
      getDirectionClasses(direction),
      responsive.sm && `sm:${getDirectionClasses(responsive.sm)}`,
      responsive.md && `md:${getDirectionClasses(responsive.md)}`,
      responsive.lg && `lg:${getDirectionClasses(responsive.lg)}`,
      responsive.xl && `xl:${getDirectionClasses(responsive.xl)}`
    ].filter(Boolean) : [getDirectionClasses(direction)]

    const stackClasses = [
      ...baseClasses,
      ...responsiveClasses,
      alignClasses[align],
      justifyClasses[justify],
      ...gapClasses[gap],
      wrapClasses[wrap],
      className
    ].join(' ')

    return (
      <div
        ref={ref}
        className={stackClasses}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Stack.displayName = 'Stack'

export { Stack }
